/* eslint-disable no-inner-declarations */
/* eslint-disable @typescript-eslint/no-namespace */
import moment, { Moment } from 'moment';
import { mapEnums, getEnumKey } from '../utils/mapping';
import * as DealModels from '../types/deal.types';
import { pick, mapValues } from 'lodash';
import { ESGCertType } from '../types/deal.types';

export function pickDates(
  data: unknown,
  fields: string[]
): Record<string, unknown> {
  return mapValues(pick(data, fields), (date) =>
    date ? moment.utc(date) : null
  );
}

export namespace MuniV3Schema {
  export enum DealType {
    competitive = 'Comp',
    negotiated = 'Neg',
    privatePlacement = 'PP'
  }

  export enum DealStatus {
    active = 'act',
    cancelled = 'can',
    postponed = 'ppd',
    notBought = 'notbght',
    bought = 'bght'
  }

  export enum OrderPeriodType {
    institutional = 'I',
    retail = 'R',
    all = 'B'
  }

  export enum SyndicateRole {
    seniorManager = 'Senior Manager',
    coSenior = 'Co-Senior Manager',
    coManager = 'Co-Manager',
    member = 'Member',
    sellingGroup = 'Selling Group Member',
    nonManager = 'Non-Manager'
  }

  export enum CouponFrequency {
    annual = 'A',
    monthly = 'M',
    quarterly = 'Q',
    semiAnnual = 'S'
  }

  export enum RatingStatus {
    underReview = 'UR',
    notAppliedFor = 'NAF',
    notAvailable = 'NA',
    rated = 'R',
    expected = 'EXP'
  }

  export enum RatingOutlook {
    steady = 'STD',
    negative = 'NEG',
    positive = 'POS'
  }

  export enum SourceOfRepayment {
    generalObligation = 'GO',
    unlimitedTaxGo = 'UTG',
    limitedTaxGo = 'LTG',
    revenue = 'R',
    certificateOfParticipation = 'COP',
    appropriation = 'APP',
    assetBackedTobacco = 'AST',
    corporateBacked = 'COR',
    mortgageHousing = 'MTG',
    taxAllocIncrement = 'TAX',
    governmentGrantBacked = 'GOV',
    personalIncomeTax = 'ITX',
    salesExciseTax = 'STX',
    fuelVehicleTax = 'FTX',
    hotelTax = 'HTX',
    cigaretteTax = 'CTX',
    miscellaneousTax = 'MTX',
    specialAssessment = 'SPA',
    carRentalTax = 'RTX',
    salesServiceLoanAgreement = 'SAL',
    lotteryGamingRevenue = 'LOT',
    doubleBarreled = 'DBB'
  }

  export enum SecurityType {
    bond = 'Bond',
    note = 'Note',
    zero = 'Zero',
    option = 'Option'
  }

  export enum CallType {
    callable = 'c',
    callableAnytime = 'ca',
    makeWholeCall = 'mwc',
    notCallable = 'nc',
    pyramid = 'py'
  }

  export enum InterestType {
    floatingRate = 'FR',
    variableRate = 'VR',
    fixedRate = 'FXR'
  }

  export enum PutType {
    mandatory = 'Mandatory',
    optional = 'Optional'
  }

  export interface Departement {
    id: number;
    name: string;
    orionId?: number;
    shortName?: string;
  }

  export interface SyndicateMember {
    id: number;
    departmentName: string;
    role: SyndicateRole;
    orionId?: number;
    shortName?: string;
    bracket?: number;
    priority?: number;
    uwDeptID?: number;
  }

  export interface OrderPeriod {
    beginDate: Moment;
    endDate: Moment;
    type: OrderPeriodType;
    orderTypes?: OrderTypes[];
  }

  export enum OrderType {
    groupNet = 'GN', //Group Net
    groupConcession = 'GC', //Group Concession
    netDesignated = 'ND', //Net Designated
    concessionDesignated = 'CD', //Concession Designated
    member = 'MEM', //Member
    preSale = 'PS', //Pre-Sale
    preSaleGroupNet = 'PSGN', //Pre-Sale Group Net
    preSaleGroupConcession = 'PSGC', //Pre-Sale Group Concession
    preSaleNetDesignated = 'PSND', //Pre-Sale Net Designated
    preSaleConcessionDesignated = 'PSCD', //Pre-Sale Concession Designated
    retail = 'RTL' //Retail
  }

  export enum ESGCertVerifier {
    Kestrel = 'Kestrel',
    EY = 'EY',
    BAMGreenStar = 'BamGreenStar',
    FirstEnvironment = 'FirstEnvironment',
    Sustainalytics = 'Sustainalytics',

    Other = 'Other',
    Self = 'Self'
  }

  export interface OrderTypes {
    orderType: OrderType;
    priority: number;
  }

  export interface RetailCategory {
    id: number;
    name: string;
    code: string;
    groupName: string;
    priority: number;
  }

  export interface GamedaySettings {
    enabled: boolean;
    allowExport: boolean;
    showAccountNames: boolean;
  }

  export interface Rating {
    agency: string;
    rating: string;
    shortTerm: string;
    status?: RatingStatus;
    outlook?: RatingOutlook;
    freeText?: string;
    effectiveDate?: Moment;
  }

  export interface CallFeature {
    securityType: SecurityType;
    displayText: string;
    type: CallType;
    firstCallDate?: Moment;
    firstCallPrice?: number;
    lastCallDate?: Moment;
    lastCallPrice?: number;
    maxPrice?: number;
    percentInc?: number;
    freeText?: string;
    callFeatureSchedules?: CallFeatureSchedule[];
  }

  export interface CallFeatureSchedule {
    callDate: Moment;
    callPrice: number;
  }

  export interface Purpose {
    enabled: boolean;
    amount: number;
  }

  export interface ESGCertification {
    esgCertType: DealModels.ESGCertType;
    esgCertVerifier: ESGCertVerifier;
    esgPreapprovedInd: boolean;
  }

  export interface Documents {
    id: string;
    docName: string;
    docType: string;
    createdDateTime: string;
    lastUpdatedDateTime: string;
  }

  export interface Deal {
    id: number;
    releasedVersion?: DealVersion;
    workingVersion?: DealVersion;
    publicVersion?: DealVersion;
  }

  export interface SeriesSecurityType {
    securityType: SecurityType;
  }

  interface BasicDealVersion {
    id: string;
    dealType: DealType;
    size: number;
    description: string;
    bankQualifiedInd: boolean;
    seniorManagerDepartment: Departement;
    issuerDepartment: Departement;
    obligor: Departement;
    financialAdvisors: Departement[];
    bondCounsels: Departement[];
    saleDate: Moment;
    saleDateTimeZone: string;
    expectedPricingDate: Moment;
    weekOfInd: boolean;
    dayToDayInd: boolean;
    toBeDeterminedInd: boolean;
    dealStatus: DealStatus;
    orderPeriod: OrderPeriod;
    repricingOrderPeriod: OrderPeriod;
    syndicate?: SyndicateMember[];
    pricingCommitted: boolean;
    assignedUsers: string[];
    compositeDeal?: boolean;
    soleMgd?: boolean;
    issueVersion?: string;
    roadshowURL: string;
    posURL: string;
    parityPlus?: boolean;
  }

  export interface BasicDeal extends BasicDealVersion {
    series: BasicSeries[];
    maturities: BasicMaturity[];
  }

  export interface DealVersion extends BasicDealVersion {
    posURL: string;
    roadshowURL: string;
    documents: Documents[];
    categories: RetailCategory[];
    gamedaySettings: GamedaySettings;
    series: Series[];
    maturities: Maturity[];
  }

  export interface BasicSeries {
    globalIssueId: string;
    id: string | number;
    size: number;
    description: string;
    code: string;
    state: string;
    altMinTaxInd: boolean;
    taxableInd: boolean;
    extraordinaryCallInd: boolean;
    ratings: Rating[];
    ratingsUnderlying: Rating[];
    insurer: string;
    sourceOfRepayment: SourceOfRepayment;
    callFeatures: CallFeature[];
    subSector?: string[];
    subSectors?: string[];
    refunding: Purpose;
    remarketing: Purpose;
    advRefunding: Purpose;
    newMoney: Purpose;
    interestType: InterestType;
    corpCusip: boolean;
    freeTextHdr: string;
    qibInd: boolean;
    securityTypes: SeriesSecurityType[];
    isBankQualified?: boolean;
    bankQualifiedInd?: boolean;
    esgCertification?: ESGCertification;

    esgCertVerifier?: ESGCertVerifier;
    esgCertType?: ESGCertType;
    conduitIssuerDepartment: Departement;
    obligor: Departement;
    forwardInd: boolean;
    taxableStateInd: boolean;
    altMinTaxStateInd: boolean;
    structure: string;
  }

  export interface Series extends BasicSeries {
    firstCouponDate: Moment;
    couponFrequency: CouponFrequency;
    datedDate: Moment;
    deliveryDate: Moment;
    settleDate: Moment;
    awardDate: Moment;
    initialTradeDate: Moment;
  }

  export interface BasicMaturity {
    id: string | number;
    seriesId: string | number;
    maturityDate: Moment;
    numberBonds: number;
    presentValue: number;
    securityType: SecurityType;
    callFeatures?: CallFeature;
    callFeature?: CallFeature;
    putMandOpt?: PutType;
    putDate?: Moment;
    price: number;
    freeText: string;
    yield: number;
    insurer: string;
    // deprecated 12/2020
    taxableTreasurySpread?: number;
    //
    taxableTsySpread?: number;
    pacBond?: DealModels.PacBondDesignation;
    yieldSpreadBump: number;
    yieldSpreadBumpValid: boolean;
  }

  export interface Maturity extends BasicMaturity {
    putDate: Moment;
    orderPeriodTerminated: boolean;
    coupon: number;
    couponFreq: CouponFrequency;
    dayCount: string;
    priceToMaturity: number;
    yieldToMaturity: number;
    price: number;
    sealedBidInd: boolean;
    notReoffered: boolean;
    soldNotAvailable: boolean;
    noRetail: boolean;
    cusip: string;
    ratings: Rating[];
    sinkingFund: DealModels.SinkingFund[];
    taxableTsySpread: number;
    taxableTsyCoupon: number;
    taxableTsyYield: number;
    taxableTsyMat: string;
    putMandOpt: PutType;
    deletedInd?: boolean;
  }

  export function toDeal(apiData: Deal): DealModels.Deal {
    return {
      id: findBestGII(apiData),
      published: toDealVersion(apiData.releasedVersion),
      working: toDealVersion(apiData.workingVersion),
      public: toDealVersion(apiData.publicVersion)
    };
  }

  function findBestGII(data: Deal): string {
    return (
      findGIIFromVersion(data.publicVersion) ||
      findGIIFromVersion(data.releasedVersion) ||
      findGIIFromVersion(data.workingVersion)
    );
  }
  export function monitorDealToDeal(apiData: DealVersion): DealModels.Deal {
    return {
      id: findGIIFromVersion(apiData),
      published: toDealVersion(apiData)
    };
  }

  export function toManagerDeal(apiData: DealVersion): DealModels.Deal {
    return {
      id: findGIIFromVersion(apiData),
      published: {
        ...toDealVersion(apiData),
        issueVersion: apiData.issueVersion
      }
    };
  }

  export function toBidcompDeal(apiData: DealVersion): DealModels.Deal {
    return {
      id: findGIIFromVersion(apiData),
      published: {
        ...toDealVersion(apiData),
        issueVersion: apiData.issueVersion
      }
    };
  }

  function findGIIFromVersion(data?: DealVersion) {
    // Buyside returns GII as deal version id.
    // Sellside you need to get it from the first series.
    return (
      (data && data.id) ||
      (data && data.series && data.series[0] && data.series[0].globalIssueId)
    );
  }

  export function toBasicDeal(apiData: BasicDeal): DealModels.BasicDeal {
    return {
      ...toBasicDealVersion(apiData),
      series: apiData.series.map(toBasicSeries),
      maturities: apiData.maturities
        ? apiData.maturities.map(toBasicMaturity)
        : []
    };
  }

  export function toDealVersion(apiData: DealVersion): DealModels.DealVersion {
    if (apiData) {
      return {
        ...toBasicDealVersion(apiData),
        ...pick(apiData, ['gamedaySettings']),
        posURL: apiData.posURL,
        roadshowURL: apiData.roadshowURL,
        documents:
          apiData.documents && apiData.documents.length
            ? apiData.documents.map((d) => ({
                id: d.id,
                docName: d.docName,
                docType: d.docType
              }))
            : null,
        categories: apiData.categories,
        series: apiData.series.map(toSeries),
        maturities: apiData.maturities
          ? apiData.maturities
              .filter(
                (mat) => typeof mat.deletedInd !== 'boolean' || !mat.deletedInd
              )
              .map(toMaturity)
          : [],
        id: findGIIFromVersion(apiData)
      } as DealModels.DealVersion;
    } else {
      return null;
    }
  }

  function toBasicDealVersion(
    apiData: BasicDealVersion
  ): DealModels.BasicDealVersion {
    return {
      ...pick(apiData, [
        'id',
        'size',
        'description',
        'saleDateTimeZone',
        'assignedUsers',
        'metrics',
        'compositeDeal',
        'obligor',
        'roadshowURL',
        'posURL',
        'documents'
      ]),
      ...pickDates(apiData, ['saleDate', 'expectedPricingDate']),
      pricingCommitted: Boolean(apiData.pricingCommitted), // TODO: remove default
      isBankQualified: apiData.bankQualifiedInd,
      isWeekOf: apiData.weekOfInd,
      isDayToDay: apiData.dayToDayInd,
      isTBD: apiData.toBeDeterminedInd,
      seniorManager: apiData.seniorManagerDepartment
        ? {
            ...apiData.seniorManagerDepartment,
            chartManagerName:
              apiData.seniorManagerDepartment.shortName ||
              apiData.seniorManagerDepartment.name
          }
        : apiData.seniorManagerDepartment,
      issuer: apiData.issuerDepartment,
      financialAdvisors:
        (apiData as any).financialAdvisor || apiData.financialAdvisors,
      bondCounsels: (apiData as any).bondCounsel || apiData.bondCounsels,
      dealType: mapEnums(DealType, DealModels.DealType, apiData.dealType),
      status: mapEnums(DealStatus, DealModels.DealStatus, apiData.dealStatus),
      orderPeriod: toOrderPeriod(apiData.orderPeriod),
      repricingOrderPeriod: toOrderPeriod(apiData.repricingOrderPeriod),
      soleMgd: Object.prototype.hasOwnProperty.call(apiData, 'soleMgd')
        ? apiData.soleMgd
        : null,
      syndicate:
        (!!apiData.syndicate &&
          apiData.syndicate.map((member) => toSyndicateMember(member))) ||
        [],
      parityPlus: apiData.parityPlus
    } as DealModels.BasicDealVersion;
  }

  function toBasicSeries(apiData: BasicSeries): DealModels.BasicSeries {
    return {
      ...pick(apiData, [
        'globalIssueId',
        'size',
        'description',
        'code',
        'state',
        'insurer',
        'refunding',
        'remarketing',
        'corpCusip',
        'obligor',
        'forwardInd',
        'structure'
      ]),
      ...pickDates(apiData, ['firstCouponDate', 'datedDate']),
      id: apiData.id.toString(),
      sourceOfRepayment: mapEnums(
        SourceOfRepayment,
        DealModels.SourceOfRepayment,
        apiData.sourceOfRepayment
      ),
      ratings: apiData.ratings
        ? apiData.ratings.map(toRating).filter((rating) => !!rating)
        : [],
      underlyingRatings: apiData.ratingsUnderlying
        ? apiData.ratingsUnderlying.map(toRating).filter((rating) => !!rating)
        : [],
      callFeatures: apiData.callFeatures
        ? apiData.callFeatures.map(toCallFeature)
        : [],
      advancedRefunding: apiData.advRefunding,
      subSectors: apiData.subSector || apiData.subSectors,
      isAMT: apiData.altMinTaxInd,
      isTaxable: apiData.taxableInd,
      taxableStateInd: apiData.taxableStateInd,
      altMinTaxStateInd: apiData.altMinTaxStateInd,
      isExtraordinaryCall: apiData.extraordinaryCallInd,
      isQIB: apiData.qibInd,
      interestType: mapEnums(
        InterestType,
        DealModels.InterestType,
        apiData.interestType
      ),
      freeTextHdr: apiData.freeTextHdr || undefined,
      securityTypes: (apiData.securityTypes || []).map((st) =>
        mapEnums(DealModels.SecurityType, SecurityType, st.securityType)
      ),
      esgCertification: toESG(apiData),
      isBankQualified: toBankQualified(apiData),
      conduitIssuerDepartment: apiData.conduitIssuerDepartment || undefined
    } as DealModels.BasicSeries;
  }

  function toBankQualified(apiData: BasicSeries): boolean {
    let isBankQualified: boolean;

    if (Object.prototype.hasOwnProperty.call(apiData, 'isBankQualified')) {
      isBankQualified = apiData.isBankQualified;
    } else if (
      Object.prototype.hasOwnProperty.call(apiData, 'bankQualifiedInd')
    ) {
      isBankQualified = apiData.bankQualifiedInd;
    }

    return isBankQualified;
  }

  function toSeries(apiData: Series): DealModels.Series {
    return {
      ...toBasicSeries(apiData),
      ...pickDates(apiData, [
        'datedDate',
        'deliveryDate',
        'settleDate',
        'awardDate',
        'initialTradeDate'
      ]),
      couponFrequency: mapEnums(
        CouponFrequency,
        DealModels.CouponFrequency,
        apiData.couponFrequency
      )
    } as DealModels.Series;
  }

  function toBasicMaturity(apiData: BasicMaturity): DealModels.BasicMaturity {
    return {
      ...pick(apiData, [
        'presentValue',
        'coupon',
        'yield',
        'insurer',
        'taxableTsyMat',
        'pacBond',
        'yieldSpreadBump',
        'yieldSpreadBumpValid'
      ]),
      ...pickDates(apiData, ['maturityDate', 'putDate']),
      id: apiData.id.toString(),
      seriesId: apiData.seriesId.toString(),
      numberBonds: apiData.numberBonds,
      size: 1000 * (apiData.numberBonds || 0),
      securityType: mapEnums(
        SecurityType,
        DealModels.SecurityType,
        apiData.securityType
      ),
      callFeature:
        apiData.callFeature || apiData.callFeatures
          ? toCallFeature(apiData.callFeature || apiData.callFeatures)
          : null,
      chartDate: toChartDate(
        apiData.putDate,
        apiData.putMandOpt,
        apiData.maturityDate
      ),
      putMandOpt: mapEnums(PutType, DealModels.PutType, apiData.putMandOpt),
      price: apiData.price,
      freeText: apiData.freeText || undefined,
      // note: taxableTreasurySpread deprecated 12/2020
      taxableTreasurySpread: apiData.pacBond
        ? null
        : apiData.taxableTreasurySpread || apiData.taxableTsySpread || undefined
    } as DealModels.BasicMaturity;
  }

  function toChartDate(putDate, putMandOpt, maturityDate) {
    return !!putDate && !!putMandOpt && putMandOpt === PutType.mandatory
      ? moment.utc(putDate)
      : moment.utc(maturityDate);
  }

  function toMaturity(apiData: Maturity): DealModels.Maturity {
    return {
      ...toBasicMaturity(apiData),
      ...pick(apiData, [
        'dayCount',
        'yield',
        'priceToMaturity',
        'yieldToMaturity',
        'price',
        'insurer',
        'cusip',
        'noRetail',
        'taxableTsyCoupon',
        'deletedInd',
        'yieldSpreadBump',
        'yieldSpreadBumpValid'
      ]),
      ...pickDates(apiData, ['putDate', '']),
      couponFrequency: mapEnums(
        CouponFrequency,
        DealModels.CouponFrequency,
        apiData.couponFreq
      ),
      ratings: (apiData.ratings || [])
        .map(toRating)
        .filter((rating) => !!rating),
      taxableTsySpread: apiData.pacBond ? null : apiData.taxableTsySpread,
      isSealedBid: apiData.sealedBidInd,
      isNotReoffered: apiData.notReoffered,
      isSoldNotAvailable: apiData.soldNotAvailable,
      sinkingFund: apiData.sinkingFund || [],
      isOrderPeriodTerminated: apiData.orderPeriodTerminated
    } as DealModels.Maturity;
  }

  function toOrderPeriod(apiData: OrderPeriod): DealModels.OrderPeriod {
    if (apiData) {
      const orderPeriodBeginDate =
        apiData.beginDate || (apiData as any).startDate;

      return {
        ...pickDates(apiData, ['endDate']),
        beginDate: orderPeriodBeginDate
          ? moment.utc(orderPeriodBeginDate)
          : null,
        type: mapEnums(
          OrderPeriodType,
          DealModels.OrderPeriodType,
          apiData.type
        ),
        orderTypes: apiData.orderTypes as any
      } as DealModels.OrderPeriod;
    } else {
      return null;
    }
  }

  function toSyndicateMember(
    apiData: SyndicateMember
  ): DealModels.SyndicateMember {
    return {
      ...pick(apiData, [
        'id',
        'departmentName',
        'orionId',
        'shortName',
        'bracket',
        'priority'
      ]),
      deptId: apiData.uwDeptID,
      role: mapEnums(SyndicateRole, DealModels.SyndicateRole, apiData.role)
    } as DealModels.SyndicateMember;
  }

  export function toRating(apiData: Rating): DealModels.Rating {
    const status = mapEnums(
      RatingStatus,
      DealModels.RatingStatus,
      apiData.status
    );
    // NR should be treated as unrated
    const isNR =
      status === DealModels.RatingStatus.rated && apiData.rating === 'NR';

    if (isNR) {
      return null;
    } else {
      const key: string =
        getEnumKey(RatingOutlook, apiData.outlook) ||
        getEnumKey(DealModels.RatingOutlook, apiData.outlook);

      return {
        ...pick(apiData, ['agency', 'rating', 'shortTerm', 'freeText']),
        ...pickDates(apiData, ['effectiveDate']),
        status: status,
        outlook: DealModels.RatingOutlook[key] || null
      };
    }
  }

  function toCallFeature(apiData: CallFeature): DealModels.CallFeature {
    return {
      ...pick(apiData, [
        'displayText',
        'freeText',
        'firstCallPrice',
        'lastCallPrice',
        'maxPrice'
      ]),
      ...pickDates(apiData, ['firstCallDate', 'lastCallDate']),
      securityType: mapEnums(
        SecurityType,
        DealModels.SecurityType,
        apiData.securityType
      ),
      type: mapEnums(CallType, DealModels.CallType, apiData.type),
      percentIncrease: apiData.percentInc,
      callFeatureSchedules: apiData.callFeatureSchedules
        ? apiData.callFeatureSchedules.map(toCallFeatureSchedule)
        : null
    };
  }

  function toCallFeatureSchedule(
    apiData: CallFeatureSchedule
  ): DealModels.CallFeatureSchedule {
    return {
      ...pick(apiData, ['callPrice']),
      ...pickDates(apiData, ['callDate'])
    } as DealModels.CallFeatureSchedule;
  }

  function toESG(api: BasicSeries) {
    if (api.esgCertification && api.esgCertification.esgCertType) {
      return {
        ...api.esgCertification,
        esgCertType: toESGType(api.esgCertification.esgCertType),
        esgCertVerifier: mapEnums(
          ESGCertVerifier,
          DealModels.ESGCertVerifier,
          api.esgCertification.esgCertVerifier
        )
      };
    } else if (api.esgCertVerifier) {
      return {
        esgCertVerifier: mapEnums(
          ESGCertVerifier,
          DealModels.ESGCertVerifier,
          api.esgCertVerifier
        ),
        esgCertType: toESGType(api.esgCertType),
        esgPreapprovedInd: null
      };
    } else {
      return undefined;
    }
  }

  function toESGType(type) {
    return type === 'Sustainable' ? 'Sustainability' : type;
  }
}

export namespace BuysideV1Schema {
  export enum OrderPeriodType {
    institutional = 'I',
    retail = 'R',
    all = 'B'
  }

  export enum SyndicateRole {
    seniorManager = 'SM',

    coSenior = 'CS',

    coManager = 'CM',

    member = 'MEM',

    sellingGroup = 'SG',

    nonManager = 'NM'
  }

  export interface Deal {
    releasedVersion: BuysideDealVersion;
  }

  export enum DealStatus {
    active = 'Act',
    cancelled = 'Can',
    postponed = 'Ppd',
    notBought = 'Notbght',
    bought = 'Bght'
  }

  export interface BuysideDealVersion extends MuniV3Schema.DealVersion {
    dealStatus: DealStatus;
  }

  export function toMuniV3SchemaDealVersion(
    apiData: BuysideDealVersion
  ): MuniV3Schema.DealVersion {
    return {
      ...apiData,
      dealStatus: mapEnums(
        DealStatus,
        MuniV3Schema.DealStatus,
        apiData.dealStatus
      )
    };
  }

  export function toDealVersion(dealVersion: BuysideDealVersion) {
    const muniV3SchemaDealVersion = toMuniV3SchemaDealVersion(dealVersion);
    return MuniV3Schema.toDealVersion(muniV3SchemaDealVersion);
  }

  export function toDeal(apiData: Deal): DealModels.Deal {
    return {
      id: apiData.releasedVersion.id,
      published: toDealVersion(apiData.releasedVersion),
      working: null
    };
  }
}
